import { ElementRef, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment.prod';
import { Observable } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { DealerInquiriesService } from './dealer-inquiries.service';
import { AppConstants } from 'src/app/app.constants';
import { country } from 'src/_lib/interfaces/common.interface';
import { contactUsPostDataInterface } from 'src/_lib/interfaces/request-interfaces/contact-us.interface';
import { Router } from '@angular/router';
import { ProductService } from './product.service';
import { BrowserService } from 'src/_lib/service/browser.service';

@Injectable({
  providedIn: 'root',
})
export class CommanpageService {
  private endpoint: string = environment.apiUrl;
  privacyPolicyData: any = [];
  aboutUsData$: Observable<any>;
  allAboutUsData: any = [];
  termConditionData: any = [];
  allCareersData$: Observable<any>;
  helpDeskData$: Observable<any>;
  firstObj: number = this.appConstants.firstObj;
  countries: any[] = [];
  selectedCountry: string = 'US';
  states: string[] = [];
  postData = { country: this.selectedCountry };
  public searchByVehicleData: any = null;
  contactUsData$: Observable<any>;
  countryData$: Observable<any>;
  productFitToMMY: boolean = false;

  constructor(
    private appConstants: AppConstants,
    private http: HttpClient,
    private contactHttp: DealerInquiriesService,
    private router: Router,
    private productHttp: ProductService,
    private browser: BrowserService
  ) { }

  getHeaderData(): any {
    const data = this.browser.localStorage?.getItem('headerData');
    return data ? JSON.parse(data)?.[0]?.data : null;
  }

  isInventoryDisabled(): boolean {
    return this.getHeaderData()?.inventoryFlag === 0;
  }

  isShowCheckFitment(): boolean {
    return this.getHeaderData()?.showCheckFitment === '1';
  }

  isuniversalFilter(): number {
    return +(this.getHeaderData()?.showUniversalFilter === '1');
  }

  isSearchByVehicleDisabled(): any {
    const val = this.browser.localStorage?.getItem('searchByVehicleDisabled');
    return val ? JSON.parse(val) : null;
  }

  getSubtitleSetting(): string {
    return this.getHeaderData()?.SubtitleViewSetting || '';
  }

  isDefaultSubtitle(): boolean {
    return this.getSubtitleSetting() === 'use_default_subtitle_info_only';
  }

  isSubtitleHidden(): boolean {
    return this.getSubtitleSetting() === 'do_not_use_subtitle';
  }

  paypalClientIdDetails(): string {
    return this.getHeaderData()?.paypalClientId || '';
  }

  getVehicleFitmentViewSetting(){
    return this.getHeaderData()?.vehicleFitmentViewSetting === 'landing_without_category' ? 'true' : 'false'
  }

  pagenotfound() {
    // Never navigate to /pagenotfound on the server — doing so during SSR would
    // cause Angular Universal to serialize the 404 page HTML and send it as the
    // HTTP response for a valid URL (e.g. /lighting/c42), breaking view-source
    // and search engine indexing. Client-side hydration fixes the visual state
    // but the damage is already done by the time the browser receives the HTML.
    if (!this.browser.isBrowser) return;
    this.router.navigate(['/pagenotfound']);
  }

  commanpageList() {
    return this.http.get(this.endpoint + 'footer-menu-content');
  }

  aboutContent() {
    if (!this.aboutUsData$) {
      this.aboutUsData$ = this.http
        .get(this.endpoint + 'footers/About')
        .pipe(shareReplay(1));
    }
    return this.aboutUsData$;
  }

  termConditionContent() {
    return this.http.get(this.endpoint + 'footers/Terms & Conditions');
  }

  privacyPolicycontent() {
    return this.http.get(this.endpoint + 'footers/Privacy Policy');
  }

  contactUsContant() {
    if (!this.contactUsData$) {
      this.contactUsData$ = this.http
        .get(this.endpoint + 'footers/contact-us')
        .pipe(shareReplay(1));
    }
    return this.contactUsData$;
  }

  helpDeskContent() {
    if (!this.helpDeskData$) {
      this.helpDeskData$ = this.http
        .get(this.endpoint + 'help-desk/ticket-types')
        .pipe(shareReplay(1));
    }
    return this.helpDeskData$;
  }

  helpDeskSubmitForm(data: any) {
    return this.http.post(this.endpoint + 'help-desk', data);
  }

  careersContent() {
    if (!this.allCareersData$) {
      this.allCareersData$ = this.http
        .get(this.endpoint + 'footers/Careers')
        .pipe(shareReplay(1));
    }
    return this.allCareersData$;
  }

  postContactForm(data: contactUsPostDataInterface): Observable<any> {
    return this.http.post(this.endpoint + 'contact-us', data);
  }

  contactUsContent() {
    return this.http.get(this.endpoint + 'footers/');
  }

  getCountryStateData(): Observable<{ countries: any[]; states: any[] }> {
    if (!this.countryData$) {
      const data: country = { country: '' };
      this.countryData$ = this.contactHttp.countryStateDropdown(data).pipe(
        shareReplay(1),
        map((response) => {
          if (
            response &&
            response[this.firstObj]?.data?.countries &&
            response[this.firstObj]?.data?.states
          ) {
            const countries = Object.entries(
              response[this.firstObj].data.countries,
            ).map(([code, name]) => ({ code, name }));
            const states = Object.entries(
              response[this.firstObj].data.states,
            ).map(([code, name]) => ({ code, name }));
            return { countries, states };
          } else {
            throw new Error('Invalid response format');
          }
        }),
      );
    }
    return this.countryData$;
  }

  getStatesByCountry(
    countryCode: string,
  ): Observable<{ code: string; name: string }[]> {
    return this.contactHttp.countryStateDropdown({ country: countryCode }).pipe(
      map((response) => {
        if (response && response[this.firstObj]?.data?.states) {
          return Object.entries(response[this.firstObj].data.states).map(
            ([code, name]) => ({ code, name: name as string }),
          );
        } else {
          return [];
        }
      }),
    );
  }

  setUrlNameField(name: string): string {
    const specialChars = /[(),.\- :%|/]/g;
    let transformedString = name?.replaceAll(specialChars, '-');
    while (transformedString?.includes('--')) {
      transformedString = transformedString.replaceAll('--', '-');
    }
    return transformedString?.replace(/^-+|-+$/g, '');
  }

  setUrlVideoNameField(name: string): string {
    const specialChars = /[,.\-  %]/g;
    let transformedString = name?.replace(specialChars, '-');
    transformedString = transformedString?.replace(/--+/g, '-');
    transformedString = transformedString?.replace(/^-|-$/g, '');
    transformedString = transformedString?.replace(/\(|\)/g, '');
    return transformedString;
  }

  setUrlSkuNameField(skuName: string) {
    const specialChars = /[ ]/g;
    let transformedString = skuName.replace(specialChars, '-');
  }

  arraySum(arr: Array<number>): number {
    const sum = arr.reduce((acc, item) => {
      return acc + item;
    }, 0.0);
    return sum;
  }

  isEmpty(value) {
    for (let prop in value) {
      if (value.hasOwnProperty(prop)) return false;
    }
    return true;
  }

  async checkFitProductToMMY(vehicleSearchForm: any): Promise<boolean> {
    const response = await this.productHttp
      .checkProductFitToMMY(vehicleSearchForm)
      .toPromise();
    return response[this.firstObj]?.data.exists || false;
  }

  async productRedirection(name: string, product_key: number,colorkey: number=null) {
    let vehicle_name = this.browser.localStorage?.getItem('vehiclename') || '';
    const navOptions: any = {};
    if (colorkey !== undefined && colorkey !== null) {
      navOptions.queryParams = { selectedcolorkey: colorkey };
    }
    if (vehicle_name) {
      const vehiclesearchForm =
        JSON.parse(this.browser.localStorage?.getItem('vehiclesearchForm')) || '';
      vehiclesearchForm.product_key = product_key;
      if (await this.checkFitProductToMMY(vehiclesearchForm)) {
        vehicle_name =
          `-${this.setUrlNameField(vehicle_name).toLowerCase()}` || '';
      } else {
        vehicle_name = '';
      }
      name = this.setUrlNameField(name).toLowerCase();
      this.browser.localStorage?.setItem('selectedproductkey', String(product_key));
      this.router.navigate([`/${name}${vehicle_name}/p${product_key}`], navOptions);
    } else {
      name = this.setUrlNameField(name).toLowerCase();
      this.browser.localStorage?.setItem('selectedproductkey', String(product_key));
      this.router.navigate([`/${name}/p${product_key}`], navOptions);
    }
  }

  async categoryRedirection(name: string, category_key: number, brand_key?: string) {
     // let vehicle_name = this.browser.localStorage?.getItem('vehiclename') || '';
    // if(vehicle_name){
    // vehicle_name = `-${this.setUrlNameField(vehicle_name).toLowerCase()}`||'';
    // }
    name = this.setUrlNameField(name).toLowerCase();
     this.browser.localStorage?.setItem('selectedcategorykey', String(category_key));
    const queryParams: any = {};
    if (brand_key) {
      queryParams.brandKey = brand_key;
    }
    this.router.navigate([`/${name}/c${category_key}`], { queryParams });
  }

initIntersectionObserver(
  ref: ElementRef,
  loadMoreCallback: () => void,
): () => void {
  if (!this.browser.isBrowser) {
    return () => {};
  }
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        loadMoreCallback();
      }
    });
  }, {
    root: null,
    rootMargin: '200px',
    threshold: 1.0,
  });
  observer.observe(ref.nativeElement);
  return () => observer.disconnect();
}

  onResize() {
  if (!this.browser.isBrowser) {
    return 15; // default for SSR
  }
  const width = this.browser.window?.innerWidth ?? 1200;
  if (width >= 992) return 15;
  if (width >= 768) return 12;
  return 9;
}

  animateOnScroll(selector: string): void {
  if(!this.browser.isBrowser) return;
  // Get all elements that should be animated.
  const elements = document.querySelectorAll<HTMLElement>(selector);
  // Stop if no matching elements are found.
  if (!elements?.length) {
    return;
  }
  // Observe elements and trigger the animation
  // when they enter the viewport.
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        // Wait until the element becomes visible.
        if (!entry.isIntersecting) {
          return;
        }
        const el = entry.target as HTMLElement;
        // Default animation used when no animation direction is specified.
        let animationClass = 'animate__fadeInUp';
        // Apply fade-in-left animation.
        if (el.classList.contains('animate-left')) {
          animationClass = 'animate__fadeInLeft';
        }
        // Apply fade-in-right animation.
        if (el.classList.contains('animate-right')) {
          animationClass = 'animate__fadeInRight';
        }
        // Apply fade-in-up animation.
        if (el.classList.contains('animate-up')) {
          animationClass = 'animate__fadeInUp';
        }
        // Make the element visible before starting the animation.
        el.style.opacity = '1';
        // Add Animate.css classes to start the animation.
        el.classList.add(
          'animate__animated',
          animationClass
        );
        // Stop observing after the animation has been triggered once.
        observer.unobserve(el);
      });
    },
    {
      // Trigger when approximately 20% of the element is visible.
      threshold: 0.2
    }
  );

  elements.forEach((el, index) => {
    // Default delay between consecutive elements.
    let delayStep = 0.1;
    // Use a slightly larger stagger delay for upward animations.
    if (el.classList.contains('animate-up')) {
      delayStep = 0.15;
    }
    // Keep elements hidden until they enter the viewport.
    el.style.opacity = '0';
    // Add a staggered delay based on the element's position.
    el.style.animationDelay =
      `${index * delayStep}s`;
    // Start observing the element.
    observer.observe(el);
  });
}

  getGroupedPages(currentPage: number, totalPages: number): number[] {
    const groupSize = this.onResize();
    const pages: number[] = [];
    const groupIndex = Math.floor((currentPage - 1) / groupSize);
    const startPage = groupIndex * groupSize + 1;
    const endPage = Math.min(startPage + groupSize - 1, totalPages);
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    if (endPage < totalPages) {
      pages.push(-1);
      pages.push(totalPages);
    }
    return pages;
  }
}